import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ChefHat, 
  Package, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Loader2, 
  Upload, 
  Download,
  Euro,
  Clock,
  Users,
  AlertTriangle,
  Printer,
  FileSpreadsheet,
  X,
  Check,
  UtensilsCrossed,
  Coffee,
  Sun,
  Settings,
  Link,
  Link2Off,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  List,
  Filter,
  Info,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// Helper to format allergens array to display string (e.g., "7a,8b" -> "7,8t")
const formatAllergensDisplay = (allergens) => {
  if (!allergens || allergens.length === 0) return '';
  return allergens.map(code => {
    const isTrace = code.endsWith('b');
    const num = code.replace(/[ab]$/, '');
    return isTrace ? `${num}t` : num;
  }).join(',');
};

const INGREDIENT_CATEGORIES = [
  { value: 'verdure', label: 'Verdure' },
  { value: 'carni', label: 'Carni' },
  { value: 'pesce', label: 'Pesce' },
  { value: 'latticini', label: 'Latticini' },
  { value: 'cereali', label: 'Cereali e Pasta' },
  { value: 'condimenti', label: 'Condimenti' },
  { value: 'surgelati', label: 'Surgelati' },
  { value: 'altro', label: 'Altro' }
];

const UNITS = [
  { value: 'kg', label: 'Kg' },
  { value: 'g', label: 'Grammi' },
  { value: 'l', label: 'Litri' },
  { value: 'ml', label: 'ml' },
  { value: 'pz', label: 'Pezzi' },
  { value: 'conf', label: 'Confezione' }
];

export const RecipesPage = () => {
  const { 
    api, 
    user,
    canViewSettingsDishes,
    canEditSettingsDishes,
    canViewSettingsCategories,
    canEditSettingsCategories
  } = useAuth();
  const [activeTab, setActiveTab] = useState('recipes');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Ingredients state
  const [ingredients, setIngredients] = useState([]);
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [ingredientForm, setIngredientForm] = useState({
    name: '', unit: 'kg', price_per_unit: '', category: 'altro', supplier: '', notes: ''
  });
  
  // Recipes state
  const [recipes, setRecipes] = useState([]);
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [recipeForm, setRecipeForm] = useState({
    name: '', description: '', category: '', portions_produced: 1,
    preparation_time: '', cooking_time: '', procedure: '',
    allergens: [], suitable_for_diets: [], notes: '', ingredients: []
  });
  const [recipeIngredientForm, setRecipeIngredientForm] = useState({
    ingredient_id: '', quantity: '', unit: 'kg'
  });
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importType, setImportType] = useState('recipes'); // 'recipes' or 'ingredients'
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1=input, 2=preview/map, 3=importing
  const [parsedRecipes, setParsedRecipes] = useState([]);
  const [parsedIngredients, setParsedIngredients] = useState([]);
  const [categoryMapping, setCategoryMapping] = useState({}); // {originalCategory: newCategoryCode}
  const [autoMapCategories, setAutoMapCategories] = useState(true);
  const [importResults, setImportResults] = useState(null); // {success: [], errors: []}
  
  // Detail view
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Sorting and view options for recipes
  const [sortBy, setSortBy] = useState('name'); // 'name', 'category', 'date'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [viewMode, setViewMode] = useState('list'); // 'grid', 'list' - default lista
  const [filterCategory, setFilterCategory] = useState('all'); // 'all' or category code
  
  const fileInputRef = useRef(null);

  // Menu dishes state (from AdminPage)
  const [categories, setCategories] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryMealType, setNewCategoryMealType] = useState('pranzo_cena');
  const [newCategoryHasSideDish, setNewCategoryHasSideDish] = useState(false);
  const [categoryOptionsDialogOpen, setCategoryOptionsDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newOptionRecipeId, setNewOptionRecipeId] = useState('');
  const [selectedRecipeIds, setSelectedRecipeIds] = useState([]); // Multi-select
  const [recipeSearchQuery, setRecipeSearchQuery] = useState(''); // Search filter
  const [editingOption, setEditingOption] = useState(null);
  const [editOptionName, setEditOptionName] = useState('');
  const [editOptionAllergens, setEditOptionAllergens] = useState([]);
  const [editOptionRecipeId, setEditOptionRecipeId] = useState('');

  // Permissions check
  const canEditIngredients = user?.role === 'super_admin' || user?.role === 'admin_struttura' || 
    user?.permissions?.ingredients_edit;
  const canEditRecipes = user?.role === 'super_admin' || user?.role === 'admin_struttura' || 
    user?.permissions?.recipes_edit;

  // Fetch data
  const fetchIngredients = useCallback(async () => {
    try {
      const res = await api.get('/ingredients');
      setIngredients(res.data);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
    }
  }, [api]);

  const fetchRecipes = useCallback(async () => {
    try {
      const res = await api.get('/recipes');
      setRecipes(res.data);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  }, [api]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [api]);

  const fetchAllergies = useCallback(async () => {
    try {
      const res = await api.get('/allergies');
      setAllergies(res.data);
    } catch (error) {
      console.error('Error fetching allergies:', error);
    }
  }, [api]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchIngredients(), fetchRecipes(), fetchCategories(), fetchAllergies()]);
      setLoading(false);
    };
    loadData();
  }, [fetchIngredients, fetchRecipes, fetchCategories, fetchAllergies]);

  // Menu dishes handlers
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Inserisci il nome della categoria');
      return;
    }
    try {
      await api.post('/categories', {
        name: newCategoryName,
        code: newCategoryName.toLowerCase().replace(/\s+/g, '_'),
        meal_type: newCategoryMealType,
        has_side_dish: newCategoryHasSideDish
      });
      toast.success('Categoria aggiunta');
      setNewCategoryName('');
      setNewCategoryMealType('pranzo_cena');
      setNewCategoryHasSideDish(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Eliminare la categoria "${categoryName}" e tutti i suoi piatti?`)) return;
    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success('Categoria eliminata');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openCategoryOptions = (category) => {
    setSelectedCategory(category);
    setNewOptionRecipeId('');
    setSelectedRecipeIds([]);
    setRecipeSearchQuery('');
    setEditingOption(null);
    setCategoryOptionsDialogOpen(true);
  };

  const handleAddOption = async () => {
    if (!selectedCategory) return;
    
    // Support both single (legacy) and multi-select
    const recipeIdsToAdd = selectedRecipeIds.length > 0 ? selectedRecipeIds : (newOptionRecipeId ? [newOptionRecipeId] : []);
    
    if (recipeIdsToAdd.length === 0) {
      toast.error('Seleziona almeno una ricetta');
      return;
    }
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const recipeId of recipeIdsToAdd) {
      const selectedRecipeData = recipes.find(r => r.id === recipeId);
      if (!selectedRecipeData) continue;
      
      try {
        await api.post(`/categories/${selectedCategory.id}/options`, {
          name: selectedRecipeData.name,
          allergens: selectedRecipeData.allergens || [],
          recipe_id: recipeId
        });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Error adding recipe ${recipeId}:`, error);
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} piatt${successCount === 1 ? 'o aggiunto' : 'i aggiunti'} al menu`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} piatt${errorCount === 1 ? 'o non aggiunto' : 'i non aggiunti'} (potrebbero essere già presenti)`);
    }
    
    // Reset
    setNewOptionRecipeId('');
    setSelectedRecipeIds([]);
    setRecipeSearchQuery('');
    
    fetchCategories();
    const updated = await api.get('/categories');
    const cat = updated.data.find(c => c.id === selectedCategory.id);
    if (cat) setSelectedCategory(cat);
  };

  const startEditOption = (option) => {
    setEditingOption(option);
    setEditOptionName(option.name);
    setEditOptionAllergens(option.allergens || []);
    setEditOptionRecipeId(option.recipe_id || '');
  };

  const handleSaveEditOption = async () => {
    if (!selectedCategory || !editingOption || !editOptionName.trim()) return;
    try {
      await api.put(`/categories/${selectedCategory.id}/options/${editingOption.id}`, {
        name: editOptionName,
        allergens: editOptionAllergens,
        recipe_id: editOptionRecipeId || null
      });
      toast.success('Piatto aggiornato');
      setEditingOption(null);
      setEditOptionRecipeId('');
      fetchCategories();
      const updated = await api.get('/categories');
      const cat = updated.data.find(c => c.id === selectedCategory.id);
      if (cat) setSelectedCategory(cat);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!selectedCategory) return;
    try {
      await api.delete(`/categories/${selectedCategory.id}/options/${optionId}`);
      toast.success('Piatto eliminato');
      fetchCategories();
      const updated = await api.get('/categories');
      const cat = updated.data.find(c => c.id === selectedCategory.id);
      if (cat) setSelectedCategory(cat);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Ingredient handlers
  const handleIngredientSubmit = async (e) => {
    e.preventDefault();
    if (!ingredientForm.name.trim()) {
      toast.error('Inserisci il nome dell\'ingrediente');
      return;
    }

    try {
      const data = {
        ...ingredientForm,
        price_per_unit: parseFloat(ingredientForm.price_per_unit) || 0
      };

      if (editingIngredient) {
        await api.put(`/ingredients/${editingIngredient.id}`, data);
        toast.success('Ingrediente aggiornato');
      } else {
        await api.post('/ingredients', data);
        toast.success('Ingrediente creato');
      }
      
      setIngredientDialogOpen(false);
      setEditingIngredient(null);
      setIngredientForm({ name: '', unit: 'kg', price_per_unit: '', category: 'altro', supplier: '', notes: '' });
      fetchIngredients();
      // Also refresh recipes to update food costs
      fetchRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteIngredient = async (ingredient) => {
    if (!window.confirm(`Eliminare "${ingredient.name}"?`)) return;
    
    try {
      await api.delete(`/ingredients/${ingredient.id}`);
      toast.success('Ingrediente eliminato');
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const openEditIngredient = (ingredient) => {
    setEditingIngredient(ingredient);
    setIngredientForm({
      name: ingredient.name,
      unit: ingredient.unit,
      price_per_unit: ingredient.price_per_unit.toString(),
      category: ingredient.category,
      supplier: ingredient.supplier || '',
      notes: ingredient.notes || ''
    });
    setIngredientDialogOpen(true);
  };

  // Recipe handlers
  const handleRecipeSubmit = async (e) => {
    e.preventDefault();
    if (!recipeForm.name.trim()) {
      toast.error('Inserisci il nome della ricetta');
      return;
    }

    try {
      const data = {
        ...recipeForm,
        portions_produced: parseInt(recipeForm.portions_produced) || 1,
        preparation_time: parseInt(recipeForm.preparation_time) || null,
        cooking_time: parseInt(recipeForm.cooking_time) || null
      };

      if (editingRecipe) {
        await api.put(`/recipes/${editingRecipe.id}`, data);
        toast.success('Ricetta aggiornata');
      } else {
        await api.post('/recipes', data);
        toast.success('Ricetta creata');
      }
      
      setRecipeDialogOpen(false);
      setEditingRecipe(null);
      resetRecipeForm();
      fetchRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const resetRecipeForm = () => {
    setRecipeForm({
      name: '', description: '', category: '', portions_produced: 1,
      preparation_time: '', cooking_time: '', procedure: '',
      allergens: [], suitable_for_diets: [], notes: '', ingredients: []
    });
    setRecipeIngredientForm({ ingredient_id: '', quantity: '', unit: 'kg' });
  };

  const handleDeleteRecipe = async (recipe) => {
    if (!window.confirm(`Eliminare la ricetta "${recipe.name}"?`)) return;
    
    try {
      await api.delete(`/recipes/${recipe.id}`);
      toast.success('Ricetta eliminata');
      fetchRecipes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const openEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setRecipeForm({
      name: recipe.name,
      description: recipe.description || '',
      category: recipe.category,
      portions_produced: recipe.portions_produced,
      preparation_time: recipe.preparation_time?.toString() || '',
      cooking_time: recipe.cooking_time?.toString() || '',
      procedure: recipe.procedure || '',
      allergens: recipe.allergens || [],
      suitable_for_diets: recipe.suitable_for_diets || [],
      notes: recipe.notes || '',
      ingredients: recipe.ingredients || []
    });
    setRecipeDialogOpen(true);
  };

  const addIngredientToRecipe = () => {
    if (!recipeIngredientForm.ingredient_id || !recipeIngredientForm.quantity) {
      toast.error('Seleziona ingrediente e quantità');
      return;
    }
    
    const ingredient = ingredients.find(i => i.id === recipeIngredientForm.ingredient_id);
    if (!ingredient) return;
    
    // Check if already added
    if (recipeForm.ingredients.some(i => i.ingredient_id === ingredient.id)) {
      toast.error('Ingrediente già aggiunto');
      return;
    }
    
    setRecipeForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, {
        ingredient_id: ingredient.id,
        ingredient_name: ingredient.name,
        quantity: parseFloat(recipeIngredientForm.quantity),
        unit: recipeIngredientForm.unit
      }]
    }));
    
    setRecipeIngredientForm({ ingredient_id: '', quantity: '', unit: 'kg' });
  };

  const removeIngredientFromRecipe = (ingredientId) => {
    setRecipeForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(i => i.ingredient_id !== ingredientId)
    }));
  };

  // Import handlers
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setImportData(text);
    };
    reader.readAsText(file);
  };

  // Improved CSV parser that handles quoted values and multiline fields
  const parseCSV = (csvText) => {
    try {
      // First, we need to handle multiline values inside quotes
      // Split into records properly handling quoted newlines
      const records = [];
      let currentRecord = '';
      let inQuotes = false;
      
      for (let i = 0; i < csvText.length; i++) {
        const char = csvText[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
          currentRecord += char;
        } else if ((char === '\n' || char === '\r') && !inQuotes) {
          if (currentRecord.trim()) {
            records.push(currentRecord.trim());
          }
          currentRecord = '';
          // Skip \r\n combination
          if (char === '\r' && csvText[i + 1] === '\n') {
            i++;
          }
        } else {
          currentRecord += char;
        }
      }
      // Don't forget the last record
      if (currentRecord.trim()) {
        records.push(currentRecord.trim());
      }
      
      if (records.length < 2) return [];
      
      // Parse header
      const headers = parseCSVLine(records[0]).map(h => h.toLowerCase().trim());
      const data = [];
      
      for (let i = 1; i < records.length; i++) {
        const line = records[i];
        if (!line) continue;
        
        try {
          const values = parseCSVLine(line);
          const row = {};
          headers.forEach((header, idx) => {
            // Clean the value: remove extra newlines inside, trim
            let val = values[idx] || '';
            val = val.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
            row[header] = val;
          });
          // Add all non-empty rows - we'll filter later after normalization
          if (Object.values(row).some(v => v && v.trim())) {
            data.push(row);
          }
        } catch (lineError) {
          console.warn(`Error parsing record ${i + 1}:`, lineError);
        }
      }
      
      return data;
    } catch (error) {
      console.error('CSV parse error:', error);
      return [];
    }
  };
  
  // Parse a single CSV line, handling quoted values
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.replace(/^"|"$/g, ''));
        current = '';
      } else if (char === ';' && !inQuotes) {
        // Also support semicolon as delimiter
        result.push(current.replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.replace(/^"|"$/g, ''));
    
    return result;
  };
  
  // Auto-map category names to existing category codes
  const autoMapCategory = (originalCategory) => {
    if (!originalCategory) return categories[0]?.code || '';
    
    const normalized = originalCategory.toLowerCase().trim();
    
    // Direct match
    const directMatch = categories.find(c => 
      c.code === normalized || c.name.toLowerCase() === normalized
    );
    if (directMatch) return directMatch.code;
    
    // Partial match
    const partialMatch = categories.find(c => 
      c.code.includes(normalized) || 
      normalized.includes(c.code) ||
      c.name.toLowerCase().includes(normalized) ||
      normalized.includes(c.name.toLowerCase())
    );
    if (partialMatch) return partialMatch.code;
    
    // Common mappings
    const mappings = {
      'primo': 'carboidrato',
      'primi': 'carboidrato',
      'pasta': 'carboidrato',
      'riso': 'carboidrato',
      'secondo': 'carne',
      'secondi': 'carne',
      'contorno carboidrato': 'carboidrato',
      'contorno verdura': 'verdura',
      'contorni carboidrato': 'carboidrato',
      'contorni verdura': 'verdura',
      'patate': 'carboidrato',
      'polenta': 'carboidrato',
      'insalata': 'verdura',
      'verdure': 'verdura',
      'contorno': 'verdura', // default contorno to verdura
      'contorni': 'verdura',
      'dessert': 'dolce',
      'frutta': 'dolce',
      'bevanda': 'bevande_calde',
      'bevande': 'bevande_calde',
    };
    
    for (const [key, value] of Object.entries(mappings)) {
      if (normalized.includes(key)) {
        const mapped = categories.find(c => c.code === value);
        if (mapped) return mapped.code;
      }
    }
    
    // Default to first category
    return categories[0]?.code || '';
  };

  // Step 1: Parse and preview
  const handleParseCSV = () => {
    if (!importData.trim()) {
      toast.error('Incolla i dati CSV');
      return;
    }
    
    try {
      const parsed = parseCSV(importData);
      
      if (parsed.length === 0) {
        toast.error('Nessun dato valido trovato. Verifica il formato CSV.');
        return;
      }
      
      // Helper to find field value with multiple possible column names
      const getField = (row, ...possibleNames) => {
        // Normalize a string for comparison (lowercase, remove extra spaces, slashes)
        const normalize = (s) => s.toLowerCase().replace(/[\/\-_\s]+/g, ' ').trim();
        
        for (const name of possibleNames) {
          const normalizedName = normalize(name);
          
          // Check all row keys
          for (const key of Object.keys(row)) {
            const normalizedKey = normalize(key);
            
            // Exact match after normalization
            if (normalizedKey === normalizedName && row[key]) {
              return row[key];
            }
            
            // Key contains the search term or vice versa
            if ((normalizedKey.includes(normalizedName) || normalizedName.includes(normalizedKey)) && row[key]) {
              return row[key];
            }
            
            // Check if any word in the key matches any word in the search term
            const keyWords = normalizedKey.split(' ');
            if (keyWords.some(w => w === normalizedName || normalizedName.includes(w)) && row[key]) {
              return row[key];
            }
          }
        }
        return '';
      };
      
      // Normalize field names - support multiple naming conventions
      const normalizedData = parsed.map(row => ({
        name: getField(row, 'name', 'nome', 'ricetta', 'preparazione', 'piatto', 'titolo'),
        category: getField(row, 'category', 'categoria', 'tipo'),
        description: getField(row, 'description', 'descrizione', 'ingredienti', 'dettagli'),
        procedure: getField(row, 'procedure', 'procedimento', 'note'),
        portions_produced: parseInt(getField(row, 'portions_produced', 'porzioni', 'portions', 'dose', 'dosi') || '1') || 1,
        allergens: getField(row, 'allergens', 'allergeni'),
        notes: getField(row, 'notes', 'note', 'appunti')
      }));
      
      // Filter out empty rows
      const validData = normalizedData.filter(r => r.name && r.name.trim());
      
      if (validData.length === 0) {
        toast.error('Nessuna ricetta valida trovata. Assicurati che il CSV contenga una colonna "Nome", "Ricetta" o "Piatto".');
        return;
      }
      
      setParsedRecipes(validData);
      
      // Auto-map categories
      const mapping = {};
      const uniqueCategories = [...new Set(validData.map(r => r.category).filter(Boolean))];
      uniqueCategories.forEach(cat => {
        mapping[cat] = autoMapCategory(cat);
      });
      setCategoryMapping(mapping);
      
      setImportStep(2);
      toast.success(`Trovate ${validData.length} ricette`);
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Errore nel parsing del CSV. Verifica il formato.');
    }
  };

  // Step 2: Execute import with detailed error tracking
  const handleImport = async () => {
    if (parsedRecipes.length === 0) {
      toast.error('Nessuna ricetta da importare');
      return;
    }
    
    setImporting(true);
    setImportStep(3);
    
    const results = { success: [], errors: [] };
    
    for (let i = 0; i < parsedRecipes.length; i++) {
      const recipe = parsedRecipes[i];
      try {
        // Validate required fields
        if (!recipe.name || !recipe.name.trim()) {
          throw new Error('Nome ricetta mancante');
        }
        
        // Apply category mapping
        const mappedCategory = categoryMapping[recipe.category] || categories[0]?.code || 'carne';
        
        // Parse allergens string to array
        let allergensArray = [];
        if (recipe.allergens) {
          allergensArray = recipe.allergens.split(/[,;]/).map(a => a.trim()).filter(Boolean);
        }
        
        await api.post('/recipes', {
          name: recipe.name.trim(),
          description: recipe.description || '',
          category: mappedCategory,
          portions_produced: recipe.portions_produced || 1,
          procedure: recipe.procedure || '',
          allergens: allergensArray,
          notes: recipe.notes || '',
          ingredients: [],
          suitable_for_diets: []
        });
        
        results.success.push({ row: i + 1, name: recipe.name });
      } catch (err) {
        const errorMsg = err.response?.data?.detail || err.message || 'Errore sconosciuto';
        results.errors.push({ 
          row: i + 1, 
          name: recipe.name || `Riga ${i + 1}`, 
          error: errorMsg 
        });
        console.error(`Error importing row ${i + 1} (${recipe.name}):`, err);
      }
    }
    
    setImportResults(results);
    setImportStep(4); // New step: results
    setImporting(false);
    
    // Refresh data
    fetchRecipes();
    fetchCategories();
  };
  
  // Reset import dialog
  const resetImportDialog = () => {
    setImportData('');
    setParsedRecipes([]);
    setParsedIngredients([]);
    setCategoryMapping({});
    setImportStep(1);
    setImporting(false);
    setImportResults(null);
  };

  // Parse ingredients CSV
  const handleParseIngredientsCSV = () => {
    if (!importData.trim()) {
      toast.error('Incolla i dati CSV');
      return;
    }
    
    try {
      const parsed = parseCSV(importData);
      
      if (parsed.length === 0) {
        toast.error('Nessun dato valido trovato. Verifica il formato CSV.');
        return;
      }
      
      // Helper to find field value with multiple possible column names
      const getField = (row, ...possibleNames) => {
        for (const name of possibleNames) {
          if (row[name]) return row[name];
          const key = Object.keys(row).find(k => 
            k.includes(name) || name.includes(k)
          );
          if (key && row[key]) return row[key];
        }
        return '';
      };
      
      // Map unit names to system units
      const mapUnit = (unitStr) => {
        const u = (unitStr || '').toLowerCase().trim();
        if (u.includes('kg') || u.includes('chilo')) return 'kg';
        if (u.includes('gr') || u.includes('gram')) return 'g';
        if (u.includes('lt') || u.includes('litr')) return 'l';
        if (u.includes('ml')) return 'ml';
        if (u.includes('pz') || u.includes('pez') || u.includes('unit')) return 'pz';
        if (u.includes('conf')) return 'conf';
        return 'kg'; // default
      };
      
      // Map category names
      const mapIngredientCategory = (catStr) => {
        const c = (catStr || '').toLowerCase().trim();
        if (c.includes('verd') || c.includes('ortag')) return 'verdure';
        if (c.includes('carn') || c.includes('macel')) return 'carni';
        if (c.includes('pesc') || c.includes('mare') || c.includes('ittic')) return 'pesce';
        if (c.includes('latt') || c.includes('formag') || c.includes('dairy')) return 'latticini';
        if (c.includes('cereal') || c.includes('pasta') || c.includes('farin') || c.includes('riso')) return 'cereali';
        if (c.includes('condim') || c.includes('olio') || c.includes('spezie')) return 'condimenti';
        if (c.includes('surgel') || c.includes('frozen')) return 'surgelati';
        return 'altro';
      };
      
      // Normalize field names
      const normalizedData = parsed.map(row => {
        const priceStr = getField(row, 'price', 'prezzo', 'costo', 'price_per_unit', 'prezzo_unitario', 'pu');
        const price = parseFloat(priceStr.replace(',', '.').replace(/[^\d.]/g, '')) || 0;
        
        return {
          name: getField(row, 'name', 'nome', 'ingrediente', 'prodotto', 'articolo', 'descrizione'),
          unit: mapUnit(getField(row, 'unit', 'unità', 'unita', 'um', 'udm')),
          price_per_unit: price,
          category: mapIngredientCategory(getField(row, 'category', 'categoria', 'tipo', 'reparto')),
          supplier: getField(row, 'supplier', 'fornitore', 'vendor'),
          notes: getField(row, 'notes', 'note', 'appunti')
        };
      });
      
      // Filter out empty rows
      const validData = normalizedData.filter(r => r.name && r.name.trim());
      
      if (validData.length === 0) {
        toast.error('Nessun ingrediente valido trovato. Assicurati che il CSV contenga una colonna "Nome", "Ingrediente" o "Prodotto".');
        return;
      }
      
      setParsedIngredients(validData);
      setImportStep(2);
      toast.success(`Trovati ${validData.length} ingredienti`);
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Errore nel parsing del CSV. Verifica il formato.');
    }
  };

  // Import ingredients with detailed error tracking
  const handleImportIngredients = async () => {
    if (parsedIngredients.length === 0) {
      toast.error('Nessun ingrediente da importare');
      return;
    }
    
    setImporting(true);
    setImportStep(3);
    
    const results = { success: [], updated: [], errors: [] };
    
    for (let i = 0; i < parsedIngredients.length; i++) {
      const ingredient = parsedIngredients[i];
      try {
        // Validate required fields
        if (!ingredient.name || !ingredient.name.trim()) {
          throw new Error('Nome ingrediente mancante');
        }
        
        // Check if ingredient already exists by name
        const existing = ingredients.find(ing => 
          ing.name.toLowerCase().trim() === ingredient.name.toLowerCase().trim()
        );
        
        if (existing) {
          // Update price if ingredient exists
          await api.put(`/ingredients/${existing.id}`, {
            ...existing,
            price_per_unit: ingredient.price_per_unit || existing.price_per_unit,
            supplier: ingredient.supplier || existing.supplier,
            notes: ingredient.notes || existing.notes
          });
          results.updated.push({ row: i + 1, name: ingredient.name, price: ingredient.price_per_unit });
        } else {
          // Create new ingredient
          await api.post('/ingredients', {
            name: ingredient.name.trim(),
            unit: ingredient.unit,
            price_per_unit: ingredient.price_per_unit,
            category: ingredient.category,
            supplier: ingredient.supplier,
            notes: ingredient.notes
          });
          results.success.push({ row: i + 1, name: ingredient.name });
        }
      } catch (err) {
        const errorMsg = err.response?.data?.detail || err.message || 'Errore sconosciuto';
        results.errors.push({ 
          row: i + 1, 
          name: ingredient.name || `Riga ${i + 1}`, 
          error: errorMsg 
        });
        console.error(`Error importing row ${i + 1} (${ingredient.name}):`, err);
      }
    }
    
    setImportResults(results);
    setImportStep(4); // Results step
    setImporting(false);
    
    // Refresh data
    fetchIngredients();
  };

  // Print label
  const handlePrintLabel = async (recipe) => {
    try {
      const res = await api.get(`/recipes/${recipe.id}/label`);
      const labelData = res.data;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etichetta - ${labelData.recipe_name}</title>
          <style>
            @media print { @page { size: 10cm 6cm; margin: 3mm; } }
            body { font-family: Arial, sans-serif; font-size: 10px; margin: 0; padding: 5mm; }
            .label { border: 1px solid #000; padding: 3mm; max-width: 9cm; }
            .title { font-size: 14px; font-weight: bold; margin-bottom: 2mm; }
            .category { font-size: 9px; color: #666; margin-bottom: 3mm; }
            .section { margin-bottom: 2mm; }
            .section-title { font-weight: bold; font-size: 9px; }
            .ingredients { font-size: 9px; }
            .allergens { color: #c00; font-weight: bold; }
            .footer { margin-top: 3mm; font-size: 8px; border-top: 1px solid #ccc; padding-top: 2mm; }
            .cost { font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="title">${labelData.recipe_name}</div>
            <div class="category">${labelData.category} • ${labelData.portions} porzioni</div>
            
            <div class="section">
              <div class="section-title">INGREDIENTI:</div>
              <div class="ingredients">${labelData.ingredients.join(', ') || 'N/D'}</div>
            </div>
            
            ${labelData.allergens.length > 0 ? `
              <div class="section allergens">
                ALLERGENI: ${labelData.allergens.join(', ')}
              </div>
            ` : ''}
            
            <div class="footer">
              <div>Prodotto: ${labelData.production_date}</div>
              <div>${labelData.expiry_info}</div>
              <div class="cost">Costo/porzione: €${labelData.food_cost_per_portion.toFixed(2)}</div>
              <div>${labelData.structure_name}</div>
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      toast.error('Errore nella generazione dell\'etichetta');
    }
  };

  // View recipe detail
  const openRecipeDetail = (recipe) => {
    setSelectedRecipe(recipe);
    setDetailDialogOpen(true);
  };

  // Filter items by search
  const filteredIngredients = ingredients.filter(i => 
    i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRecipes = recipes.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Apply category filter and sorting
  const sortedRecipes = filteredRecipes
    .filter(r => filterCategory === 'all' || r.category === filterCategory)
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'it');
          break;
        case 'category':
          const catA = categories.find(c => c.code === a.category)?.name || a.category;
          const catB = categories.find(c => c.code === b.category)?.name || b.category;
          comparison = catA.localeCompare(catB, 'it');
          // Secondary sort by name within same category
          if (comparison === 0) comparison = a.name.localeCompare(b.name, 'it');
          break;
        case 'date':
          comparison = new Date(b.created_at || 0) - new Date(a.created_at || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Group recipes by category for list view
  const recipesByCategory = sortedRecipes.reduce((acc, recipe) => {
    const catName = categories.find(c => c.code === recipe.category)?.name || recipe.category;
    if (!acc[catName]) acc[catName] = [];
    acc[catName].push(recipe);
    return acc;
  }, {});

  // Calculate total estimated cost for recipe ingredients in form
  const calculateFormFoodCost = () => {
    let total = 0;
    recipeForm.ingredients.forEach(ing => {
      const ingredient = ingredients.find(i => i.id === ing.ingredient_id);
      if (ingredient) {
        total += ing.quantity * ingredient.price_per_unit;
      }
    });
    const portions = parseInt(recipeForm.portions_produced) || 1;
    return { total: total.toFixed(2), perPortion: (total / portions).toFixed(2) };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="recipes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
            <ChefHat className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Ricette & Ingredienti</h1>
            <p className="text-slate-500 text-sm">Gestisci il tuo ricettario e calcola i food cost</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setImportType(activeTab === 'recipes' ? 'recipes' : 'ingredients');
              setImportDialogOpen(true);
            }}
            className="border-orange-200 text-orange-700 hover:bg-orange-50"
            data-testid="import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importa CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="recipes" className="flex items-center gap-2" data-testid="recipes-tab">
            <ChefHat className="w-4 h-4" />
            <span className="hidden sm:inline">Ricette</span> ({recipes.length})
          </TabsTrigger>
          <TabsTrigger value="ingredients" className="flex items-center gap-2" data-testid="ingredients-tab">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Ingredienti</span> ({ingredients.length})
          </TabsTrigger>
          <TabsTrigger value="menu" className="flex items-center gap-2" data-testid="menu-dishes-tab">
            <UtensilsCrossed className="w-4 h-4" />
            <span className="hidden sm:inline">Menu</span> ({categories.length})
          </TabsTrigger>
        </TabsList>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="space-y-4">
          {/* Toolbar: Add + Sort + View controls - Mobile optimized */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
            {canEditRecipes && (
              <Button
                onClick={() => {
                  resetRecipeForm();
                  setEditingRecipe(null);
                  setRecipeDialogOpen(true);
                }}
                className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto"
                data-testid="add-recipe-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Ricetta
              </Button>
            )}
            
            <div className="hidden sm:flex sm:flex-1" />
            
            {/* Filters row on mobile */}
            <div className="flex gap-2 w-full sm:w-auto">
              {/* Filter by category */}
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="flex-1 sm:flex-none sm:w-[140px] h-10 sm:h-9 text-sm" data-testid="filter-category">
                  <Filter className="w-4 h-4 mr-1 sm:mr-2 text-slate-400 shrink-0" />
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Sort by */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="flex-1 sm:flex-none sm:w-[120px] h-10 sm:h-9 text-sm" data-testid="sort-by">
                  <ArrowUpDown className="w-4 h-4 mr-1 sm:mr-2 text-slate-400 shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Nome</SelectItem>
                  <SelectItem value="category">Categoria</SelectItem>
                  <SelectItem value="date">Data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sort and view controls */}
            <div className="flex gap-2">
              {/* Sort order toggle */}
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 sm:h-9 sm:w-9"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                title={sortOrder === 'asc' ? 'Crescente' : 'Decrescente'}
                data-testid="sort-order-toggle"
              >
                {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              </Button>
              
              {/* View mode toggle */}
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="icon"
                  className={`h-10 w-10 sm:h-9 sm:w-9 rounded-r-none ${viewMode === 'grid' ? 'bg-orange-600' : ''}`}
                  onClick={() => setViewMode('grid')}
                  title="Vista griglia"
                  data-testid="view-mode-grid"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="icon"
                  className={`h-10 w-10 sm:h-9 sm:w-9 rounded-l-none ${viewMode === 'list' ? 'bg-orange-600' : ''}`}
                  onClick={() => setViewMode('list')}
                  title="Vista lista"
                  data-testid="view-mode-list"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {sortedRecipes.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <ChefHat className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nessuna ricetta trovata</p>
                <p className="text-slate-400 text-sm mt-1">
                  {canEditRecipes ? 'Clicca "Nuova Ricetta" per iniziare' : 'Le ricette appariranno qui'}
                </p>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedRecipes.map(recipe => (
                <Card 
                  key={recipe.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openRecipeDetail(recipe)}
                  data-testid={`recipe-card-${recipe.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{recipe.name}</CardTitle>
                        <Badge variant="outline" className="mt-1">
                          {categories.find(c => c.code === recipe.category)?.name || recipe.category}
                        </Badge>
                      </div>
                      {canEditRecipes && (
                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handlePrintLabel(recipe)}
                            title="Stampa etichetta"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditRecipe(recipe)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteRecipe(recipe)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {recipe.portions_produced} porzioni
                      </div>
                      {recipe.preparation_time && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {recipe.preparation_time + (recipe.cooking_time || 0)} min
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <div className="flex items-center gap-1 text-green-700 font-semibold">
                        <Euro className="w-4 h-4" />
                        {(recipe.food_cost_per_portion || 0).toFixed(2)}/porzione
                      </div>
                      {recipe.allergens?.length > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          {recipe.allergens.length} allergeni
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            /* List View - Grouped by Category */
            <div className="space-y-6">
              {sortBy === 'category' ? (
                /* Grouped by category */
                Object.entries(recipesByCategory).map(([catName, catRecipes]) => (
                  <div key={catName}>
                    <h3 className="text-lg font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <ChefHat className="w-5 h-5 text-orange-500" />
                      {catName}
                      <Badge variant="secondary" className="ml-2">{catRecipes.length}</Badge>
                    </h3>
                    <div className="border rounded-lg divide-y">
                      {catRecipes.map(recipe => (
                        <div 
                          key={recipe.id}
                          className="p-3 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                          onClick={() => openRecipeDetail(recipe)}
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium">{recipe.name}</p>
                              <div className="flex items-center gap-3 text-sm text-slate-500">
                                <span>{recipe.portions_produced} porzioni</span>
                                {recipe.preparation_time && (
                                  <span>{recipe.preparation_time + (recipe.cooking_time || 0)} min</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-green-700 font-semibold">
                              €{(recipe.food_cost_per_portion || 0).toFixed(2)}
                            </span>
                            {recipe.allergens?.length > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {recipe.allergens.length}
                              </Badge>
                            )}
                            {canEditRecipes && (
                              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7" onClick={() => openEditRecipe(recipe)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-red-600" onClick={() => handleDeleteRecipe(recipe)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                /* Simple list (not grouped) */
                <div className="border rounded-lg divide-y">
                  {sortedRecipes.map(recipe => (
                    <div 
                      key={recipe.id}
                      className="p-3 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                      onClick={() => openRecipeDetail(recipe)}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{recipe.name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <Badge variant="outline" className="text-xs">
                              {categories.find(c => c.code === recipe.category)?.name || recipe.category}
                            </Badge>
                            <span>{recipe.portions_produced} porzioni</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-green-700 font-semibold">
                          €{(recipe.food_cost_per_portion || 0).toFixed(2)}
                        </span>
                        {recipe.allergens?.length > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {recipe.allergens.length}
                          </Badge>
                        )}
                        {canEditRecipes && (
                          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7" onClick={() => openEditRecipe(recipe)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-red-600" onClick={() => handleDeleteRecipe(recipe)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Ingredients Tab */}
        <TabsContent value="ingredients" className="space-y-4">
          {canEditIngredients && (
            <Button
              onClick={() => {
                setEditingIngredient(null);
                setIngredientForm({ name: '', unit: 'kg', price_per_unit: '', category: 'altro', supplier: '', notes: '' });
                setIngredientDialogOpen(true);
              }}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="add-ingredient-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Ingrediente
            </Button>
          )}

          {filteredIngredients.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nessun ingrediente trovato</p>
                <p className="text-slate-400 text-sm mt-1">
                  {canEditIngredients ? 'Clicca "Nuovo Ingrediente" o importa un CSV' : 'Gli ingredienti appariranno qui'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Nome</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Categoria</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600">Prezzo/Unità</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600">Fornitore</th>
                      {canEditIngredients && (
                        <th className="text-right px-4 py-3 font-medium text-slate-600">Azioni</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredIngredients.map(ingredient => (
                      <tr key={ingredient.id} className="hover:bg-slate-50" data-testid={`ingredient-row-${ingredient.id}`}>
                        <td className="px-4 py-3 font-medium">{ingredient.name}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="capitalize">
                            {INGREDIENT_CATEGORIES.find(c => c.value === ingredient.category)?.label || ingredient.category}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          €{ingredient.price_per_unit.toFixed(2)}/{ingredient.unit}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{ingredient.supplier || '-'}</td>
                        {canEditIngredients && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditIngredient(ingredient)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteIngredient(ingredient)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Menu Dishes Tab */}
        <TabsContent value="menu" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UtensilsCrossed className="w-5 h-5 text-blue-600" />
                Piatti del Menu
              </CardTitle>
              <CardDescription>
                {canEditSettingsDishes 
                  ? 'Configura i piatti predefiniti per ogni categoria. Gli operatori li selezioneranno velocemente durante gli ordini.'
                  : 'Visualizza i piatti predefiniti delle categorie del menu.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Add new category */}
              {canEditSettingsCategories && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6 space-y-3">
                  <Label className="text-sm font-medium text-blue-700">Aggiungi nuova categoria</Label>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Nome categoria (es: Antipasto, Zuppa...)"
                      className="flex-1 min-w-[180px] bg-white"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                      data-testid="new-category-name"
                    />
                    <Select value={newCategoryMealType} onValueChange={(v) => {
                      setNewCategoryMealType(v);
                      if (v === 'colazione') setNewCategoryHasSideDish(false);
                    }}>
                      <SelectTrigger className="w-40 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="colazione">
                          <span className="flex items-center gap-2">
                            <Coffee className="w-4 h-4 text-amber-600" />
                            Colazione
                          </span>
                        </SelectItem>
                        <SelectItem value="pranzo_cena">
                          <span className="flex items-center gap-2">
                            <Sun className="w-4 h-4 text-orange-600" />
                            Pranzo/Cena
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddCategory} className="bg-blue-700 hover:bg-blue-800" data-testid="add-category-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi
                    </Button>
                  </div>
                  {newCategoryMealType === 'pranzo_cena' && (
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="checkbox"
                        id="has_side_dish_recipes"
                        checked={newCategoryHasSideDish}
                        onChange={(e) => setNewCategoryHasSideDish(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor="has_side_dish_recipes" className="text-sm text-slate-600 cursor-pointer">
                        Ha campo contorno (come Carne, Pesce, Vegetariano)
                      </Label>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categories.filter(c => c.code !== 'contorni').map(category => (
                  <div 
                    key={category.id} 
                    className={`p-4 rounded-lg border ${
                      category.meal_type === 'colazione' 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`category-card-${category.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{category.name}</h3>
                        <p className="text-xs text-slate-500">
                          {category.meal_type === 'colazione' ? 'Colazione' : 'Pranzo/Cena'}
                          {category.has_side_dish && ' • +contorno'}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {canEditSettingsDishes && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCategoryOptions(category)}
                            className="gap-1"
                            data-testid={`manage-category-${category.id}`}
                          >
                            <Plus className="w-4 h-4" />
                            Gestisci
                          </Button>
                        )}
                        {canEditSettingsCategories && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id, category.name)}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {category.options && category.options.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {category.options.slice(0, 5).map(opt => (
                          <Badge key={opt.id} variant="outline" className="text-xs bg-white">
                            {opt.name}
                            {opt.allergens?.length > 0 && (
                              <span className="ml-1 text-amber-600 text-[10px]">({formatAllergensDisplay(opt.allergens)})</span>
                            )}
                          </Badge>
                        ))}
                        {category.options.length > 5 && (
                          <Badge variant="outline" className="text-xs bg-slate-100">
                            +{category.options.length - 5} altri
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic">Nessun piatto configurato</p>
                    )}
                  </div>
                ))}
                
                {/* Contorni section */}
                {categories.filter(c => c.code === 'contorni').map(category => (
                  <div 
                    key={category.id} 
                    className="p-4 rounded-lg border bg-green-50 border-green-200 md:col-span-2"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{category.name}</h3>
                        <p className="text-xs text-slate-500">Contorni disponibili per tutti i secondi</p>
                      </div>
                      {canEditSettingsDishes && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCategoryOptions(category)}
                          className="gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Gestisci
                        </Button>
                      )}
                    </div>
                    
                    {category.options && category.options.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {category.options.map(opt => (
                          <Badge key={opt.id} variant="outline" className="text-xs bg-white">
                            {opt.name}
                            {opt.allergens?.length > 0 && (
                              <span className="ml-1 text-amber-600 text-[10px]">({formatAllergensDisplay(opt.allergens)})</span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic">Nessun contorno configurato</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Ingredient Dialog */}
      <Dialog open={ingredientDialogOpen} onOpenChange={setIngredientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingIngredient ? 'Modifica Ingrediente' : 'Nuovo Ingrediente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIngredientSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={ingredientForm.name}
                onChange={e => setIngredientForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Es. Farina 00"
                data-testid="ingredient-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unità di misura</Label>
                <Select
                  value={ingredientForm.unit}
                  onValueChange={v => setIngredientForm(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Prezzo per unità (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={ingredientForm.price_per_unit}
                  onChange={e => setIngredientForm(prev => ({ ...prev, price_per_unit: e.target.value }))}
                  placeholder="0.00"
                  data-testid="ingredient-price-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select
                value={ingredientForm.category}
                onValueChange={v => setIngredientForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INGREDIENT_CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Fornitore</Label>
              <Input
                value={ingredientForm.supplier}
                onChange={e => setIngredientForm(prev => ({ ...prev, supplier: e.target.value }))}
                placeholder="Es. Metro"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={ingredientForm.notes}
                onChange={e => setIngredientForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
            
            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700">
              {editingIngredient ? 'Salva Modifiche' : 'Crea Ingrediente'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Recipe Dialog */}
      <Dialog open={recipeDialogOpen} onOpenChange={setRecipeDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRecipe ? 'Modifica Ricetta' : 'Nuova Ricetta'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecipeSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Nome Ricetta *</Label>
                <Input
                  value={recipeForm.name}
                  onChange={e => setRecipeForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Es. Pasta al Ragù"
                  data-testid="recipe-name-input"
                />
              </div>
              
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Categoria Menu</Label>
                <Select
                  value={recipeForm.category}
                  onValueChange={v => setRecipeForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger data-testid="recipe-category-select">
                    <SelectValue placeholder="Seleziona categoria..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length > 0 ? (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">Pranzo/Cena</div>
                        {categories.filter(c => c.meal_type !== 'colazione').map(c => (
                          <SelectItem key={c.id} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100 mt-1">Colazione</div>
                        {categories.filter(c => c.meal_type === 'colazione').map(c => (
                          <SelectItem key={c.id} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </>
                    ) : (
                      <p className="p-2 text-sm text-slate-500">Nessuna categoria. Crea prima una struttura.</p>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">La ricetta apparirà automaticamente nel Menu</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea
                value={recipeForm.description}
                onChange={e => setRecipeForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Breve descrizione della ricetta..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Porzioni prodotte *</Label>
                <Input
                  type="number"
                  min="1"
                  value={recipeForm.portions_produced}
                  onChange={e => setRecipeForm(prev => ({ ...prev, portions_produced: e.target.value }))}
                  data-testid="recipe-portions-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tempo prep. (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={recipeForm.preparation_time}
                  onChange={e => setRecipeForm(prev => ({ ...prev, preparation_time: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Tempo cottura (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={recipeForm.cooking_time}
                  onChange={e => setRecipeForm(prev => ({ ...prev, cooking_time: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Ingredients section */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <Label className="text-base font-semibold">Ingredienti</Label>
              
              <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                <Select
                  value={recipeIngredientForm.ingredient_id}
                  onValueChange={v => setRecipeIngredientForm(prev => ({ ...prev, ingredient_id: v }))}
                >
                  <SelectTrigger className="flex-1 min-w-[150px]">
                    <SelectValue placeholder="Seleziona ingrediente" />
                  </SelectTrigger>
                  <SelectContent>
                    {ingredients.map(ing => (
                      <SelectItem key={ing.id} value={ing.id}>
                        {ing.name} (€{ing.price_per_unit}/{ing.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Qtà"
                  className="w-20"
                  value={recipeIngredientForm.quantity}
                  onChange={e => setRecipeIngredientForm(prev => ({ ...prev, quantity: e.target.value }))}
                />
                
                <Select
                  value={recipeIngredientForm.unit}
                  onValueChange={v => setRecipeIngredientForm(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button type="button" onClick={addIngredientToRecipe} variant="secondary" size="icon">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {recipeForm.ingredients.length > 0 && (
                <div className="space-y-2 mt-3">
                  {recipeForm.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span>{ing.ingredient_name} - {ing.quantity} {ing.unit}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-600"
                        onClick={() => removeIngredientFromRecipe(ing.ingredient_id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="pt-2 border-t text-sm font-medium text-green-700">
                    Food Cost: €{calculateFormFoodCost().total} totale • €{calculateFormFoodCost().perPortion}/porzione
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Procedimento</Label>
              <Textarea
                value={recipeForm.procedure}
                onChange={e => setRecipeForm(prev => ({ ...prev, procedure: e.target.value }))}
                placeholder="Descrivi i passaggi per preparare la ricetta..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Allergeni (separati da virgola)</Label>
              <Input
                value={recipeForm.allergens.join(', ')}
                onChange={e => setRecipeForm(prev => ({ 
                  ...prev, 
                  allergens: e.target.value.split(',').map(a => a.trim()).filter(Boolean)
                }))}
                placeholder="Es. Glutine, Lattosio, Uova"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={recipeForm.notes}
                onChange={e => setRecipeForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>
            
            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700">
              {editingRecipe ? 'Salva Modifiche' : 'Crea Ricetta'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        setImportDialogOpen(open);
        if (!open) resetImportDialog();
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Importa {importType === 'recipes' ? 'Ricette' : 'Listino Ingredienti'} da CSV
              {importStep > 1 && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  - Step {importStep}/3
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {importStep === 1 && 'Scegli cosa importare e carica il file CSV'}
              {importStep === 2 && (importType === 'recipes' ? 'Verifica e mappa le categorie' : 'Verifica i dati prima di importare')}
              {importStep === 3 && 'Importazione in corso...'}
              {importStep === 4 && 'Risultati dell\'importazione'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step 1: Choose type and Input CSV */}
          {importStep === 1 && (
            <div className="space-y-4">
              {/* Type selector */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setImportType('recipes')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    importType === 'recipes' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <ChefHat className={`w-6 h-6 mb-2 ${importType === 'recipes' ? 'text-orange-600' : 'text-slate-400'}`} />
                  <p className="font-medium">Ricette</p>
                  <p className="text-xs text-slate-500">Importa il tuo ricettario</p>
                </button>
                <button
                  type="button"
                  onClick={() => setImportType('ingredients')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    importType === 'ingredients' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Package className={`w-6 h-6 mb-2 ${importType === 'ingredients' ? 'text-green-600' : 'text-slate-400'}`} />
                  <p className="font-medium">Listino Ingredienti</p>
                  <p className="text-xs text-slate-500">Importa prezzi per food cost</p>
                </button>
              </div>

              {/* Info message for recipes */}
              {importType === 'recipes' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button 
                      type="button"
                      className="w-full flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Info className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">Come devono essere le ricette?</p>
                        <p className="text-xs text-blue-600">Clicca per leggere le linee guida</p>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="start">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <ChefHat className="w-5 h-5 text-orange-500" />
                        <h4 className="font-semibold text-slate-900">Linee guida per le ricette</h4>
                      </div>
                      <div className="text-sm text-slate-600 space-y-2">
                        <p>
                          Ogni ricetta importata deve rappresentare un <strong>piatto completo e finito</strong>, 
                          pronto per essere servito.
                        </p>
                        <div className="p-2 bg-green-50 rounded border border-green-200">
                          <p className="text-green-800 font-medium text-xs mb-1">✓ Esempio corretto:</p>
                          <p className="text-green-700 text-xs">"Risotto ai funghi porcini" con tutti gli ingredienti necessari</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-red-800 font-medium text-xs mb-1">✗ Da evitare:</p>
                          <p className="text-red-700 text-xs">"Base risotto" + "Condimento funghi" come ricette separate da combinare</p>
                        </div>
                        <p className="text-xs text-slate-500 pt-1">
                          Questo approccio garantisce un calcolo accurato del food cost 
                          e una gestione ottimale del menu.
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                  ref={fileInputRef}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Carica file CSV
                </Button>
                
                {importType === 'recipes' ? (
                  <p className="text-xs text-slate-500 mt-3">
                    <strong>Intestazioni supportate:</strong><br/>
                    Nome: <code className="bg-slate-100 px-1 rounded">Ricetta</code>, <code className="bg-slate-100 px-1 rounded">Nome</code>, <code className="bg-slate-100 px-1 rounded">Piatto</code><br/>
                    Categoria: <code className="bg-slate-100 px-1 rounded">Categoria</code>, <code className="bg-slate-100 px-1 rounded">Tipo</code><br/>
                    Allergeni: <code className="bg-slate-100 px-1 rounded">Allergeni</code>
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-3">
                    <strong>Intestazioni supportate:</strong><br/>
                    Nome: <code className="bg-slate-100 px-1 rounded">Nome</code>, <code className="bg-slate-100 px-1 rounded">Ingrediente</code>, <code className="bg-slate-100 px-1 rounded">Prodotto</code><br/>
                    Prezzo: <code className="bg-slate-100 px-1 rounded">Prezzo</code>, <code className="bg-slate-100 px-1 rounded">Costo</code>, <code className="bg-slate-100 px-1 rounded">PU</code><br/>
                    Unità: <code className="bg-slate-100 px-1 rounded">Unità</code>, <code className="bg-slate-100 px-1 rounded">UM</code> (kg, g, l, ml, pz)
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Oppure incolla i dati (separati da virgola o punto e virgola):</Label>
                <Textarea
                  value={importData}
                  onChange={e => setImportData(e.target.value)}
                  placeholder={importType === 'recipes' 
                    ? 'Categoria,Ricetta,Ingredienti,Procedimento,Allergeni\nPasta fresca,Pasta all\'uovo,800g farina 500g tuorlo,Impastare...,Glutine Uova'
                    : 'Nome,Unità,Prezzo,Categoria,Fornitore\nFarina 00,kg,1.20,cereali,Mulino Rossi\nUova,pz,0.25,latticini,Fattoria Bio'
                  }
                  rows={8}
                  className="font-mono text-xs"
                  data-testid="import-csv-textarea"
                />
              </div>
              
              <Button
                onClick={importType === 'recipes' ? handleParseCSV : handleParseIngredientsCSV}
                disabled={!importData.trim()}
                className={`w-full ${importType === 'recipes' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}
                data-testid="parse-csv-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Analizza e Anteprima
              </Button>
            </div>
          )}
          
          {/* Step 2: Preview - RECIPES */}
          {importStep === 2 && importType === 'recipes' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <p className="font-medium text-green-800">✓ Trovate {parsedRecipes.length} ricette</p>
                  <p className="text-xs text-green-600">Verifica le categorie prima di importare</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setImportStep(1)}
                >
                  ← Indietro
                </Button>
              </div>
              
              {/* Category Mapping */}
              {Object.keys(categoryMapping).length > 0 && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Mappatura Categorie</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {Object.entries(categoryMapping).map(([original, mapped]) => (
                      <div key={original} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="text-sm text-slate-600 min-w-[120px]">"{original}"</span>
                        <span className="text-slate-400">→</span>
                        <Select
                          value={mapped}
                          onValueChange={(v) => setCategoryMapping(prev => ({ ...prev, [original]: v }))}
                        >
                          <SelectTrigger className="flex-1 h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map(c => (
                              <SelectItem key={c.id} value={c.code}>
                                {c.name} ({c.meal_type === 'colazione' ? '☕' : '🍽️'})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Preview Table */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Anteprima Ricette</Label>
                <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Nome</th>
                        <th className="p-2 text-left">Categoria Originale</th>
                        <th className="p-2 text-left">→ Categoria Menu</th>
                        <th className="p-2 text-center">Porzioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRecipes.slice(0, 20).map((recipe, idx) => (
                        <tr key={idx} className="border-t hover:bg-slate-50">
                          <td className="p-2 font-medium">{recipe.name}</td>
                          <td className="p-2 text-slate-500">{recipe.category || '-'}</td>
                          <td className="p-2">
                            <Badge variant="outline" className="text-xs">
                              {categories.find(c => c.code === categoryMapping[recipe.category])?.name || categoryMapping[recipe.category] || categories[0]?.name}
                            </Badge>
                          </td>
                          <td className="p-2 text-center">{recipe.portions_produced}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsedRecipes.length > 20 && (
                    <p className="p-2 text-center text-xs text-slate-500 bg-slate-50">
                      ... e altre {parsedRecipes.length - 20} ricette
                    </p>
                  )}
                </div>
              </div>
              
              <Button
                onClick={handleImport}
                className="w-full bg-orange-600 hover:bg-orange-700"
                data-testid="import-recipes-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Importa {parsedRecipes.length} Ricette
              </Button>
            </div>
          )}

          {/* Step 2: Preview - INGREDIENTS */}
          {importStep === 2 && importType === 'ingredients' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <p className="font-medium text-green-800">✓ Trovati {parsedIngredients.length} ingredienti</p>
                  <p className="text-xs text-green-600">Verifica i dati prima di importare</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setImportStep(1)}
                >
                  ← Indietro
                </Button>
              </div>
              
              {/* Preview Table */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Anteprima Ingredienti</Label>
                <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Nome</th>
                        <th className="p-2 text-center">Unità</th>
                        <th className="p-2 text-right">Prezzo</th>
                        <th className="p-2 text-left">Categoria</th>
                        <th className="p-2 text-left">Fornitore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedIngredients.slice(0, 30).map((ing, idx) => {
                        const existingIng = ingredients.find(i => 
                          i.name.toLowerCase().trim() === ing.name.toLowerCase().trim()
                        );
                        return (
                          <tr key={idx} className={`border-t hover:bg-slate-50 ${existingIng ? 'bg-yellow-50' : ''}`}>
                            <td className="p-2 font-medium">
                              {ing.name}
                              {existingIng && (
                                <Badge variant="outline" className="ml-2 text-[10px] bg-yellow-100">
                                  Aggiorna
                                </Badge>
                              )}
                            </td>
                            <td className="p-2 text-center">{ing.unit}</td>
                            <td className="p-2 text-right text-green-700 font-medium">
                              €{ing.price_per_unit.toFixed(2)}
                            </td>
                            <td className="p-2 text-slate-500 capitalize">{ing.category}</td>
                            <td className="p-2 text-slate-500">{ing.supplier || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {parsedIngredients.length > 30 && (
                    <p className="p-2 text-center text-xs text-slate-500 bg-slate-50">
                      ... e altri {parsedIngredients.length - 30} ingredienti
                    </p>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  💡 Gli ingredienti già esistenti (evidenziati in giallo) verranno aggiornati con i nuovi prezzi
                </p>
              </div>
              
              <Button
                onClick={handleImportIngredients}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="import-ingredients-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Importa {parsedIngredients.length} Ingredienti
              </Button>
            </div>
          )}
          
          {/* Step 3: Importing */}
          {importStep === 3 && (
            <div className="py-8 text-center">
              <Loader2 className={`w-12 h-12 mx-auto animate-spin mb-4 ${importType === 'recipes' ? 'text-orange-500' : 'text-green-500'}`} />
              <p className="text-lg font-medium">Importazione in corso...</p>
              <p className="text-sm text-slate-500">
                {importType === 'recipes' 
                  ? `Sto creando ${parsedRecipes.length} ricette`
                  : `Sto importando ${parsedIngredients.length} ingredienti`
                }
              </p>
            </div>
          )}

          {/* Step 4: Results */}
          {importStep === 4 && importResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className={`p-4 rounded-lg ${
                importResults.errors.length === 0 
                  ? 'bg-green-50 border border-green-200' 
                  : importResults.success.length === 0 && (!importResults.updated || importResults.updated.length === 0)
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center gap-3">
                  {importResults.errors.length === 0 ? (
                    <Check className="w-8 h-8 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                  )}
                  <div>
                    <p className="font-semibold text-lg">
                      {importResults.errors.length === 0 
                        ? 'Import completato con successo!' 
                        : 'Import completato con alcuni errori'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {importType === 'recipes' ? (
                        <>
                          ✓ {importResults.success.length} ricette importate
                          {importResults.errors.length > 0 && ` • ✗ ${importResults.errors.length} errori`}
                        </>
                      ) : (
                        <>
                          ✓ {importResults.success.length} nuovi ingredienti
                          {importResults.updated?.length > 0 && ` • ↻ ${importResults.updated.length} aggiornati`}
                          {importResults.errors.length > 0 && ` • ✗ ${importResults.errors.length} errori`}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error details */}
              {importResults.errors.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-red-700">
                    Righe con errori ({importResults.errors.length})
                  </Label>
                  <div className="max-h-48 overflow-y-auto border border-red-200 rounded-lg bg-red-50">
                    <table className="w-full text-xs">
                      <thead className="bg-red-100 sticky top-0">
                        <tr>
                          <th className="p-2 text-left w-12">Riga</th>
                          <th className="p-2 text-left">Nome</th>
                          <th className="p-2 text-left">Errore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResults.errors.map((err, idx) => (
                          <tr key={idx} className="border-t border-red-200">
                            <td className="p-2 font-mono">{err.row}</td>
                            <td className="p-2 font-medium truncate max-w-[120px]">{err.name}</td>
                            <td className="p-2 text-red-700">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500">
                    💡 Correggi queste righe nel CSV e importale di nuovo
                  </p>
                </div>
              )}

              {/* Success details (collapsible) */}
              {importResults.success.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-green-700 hover:underline">
                    Mostra {importResults.success.length} {importType === 'recipes' ? 'ricette' : 'ingredienti'} importat{importType === 'recipes' ? 'e' : 'i'}
                  </summary>
                  <div className="mt-2 max-h-32 overflow-y-auto border border-green-200 rounded-lg bg-green-50 p-2">
                    <ul className="text-xs space-y-0.5">
                      {importResults.success.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-1">
                          <Check className="w-3 h-3 text-green-600" />
                          <span>{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              )}

              {/* Updated ingredients (for ingredient import) */}
              {importResults.updated?.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-blue-700 hover:underline">
                    Mostra {importResults.updated.length} prezzi aggiornati
                  </summary>
                  <div className="mt-2 max-h-32 overflow-y-auto border border-blue-200 rounded-lg bg-blue-50 p-2">
                    <ul className="text-xs space-y-0.5">
                      {importResults.updated.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-1">
                          <RefreshCw className="w-3 h-3 text-blue-600" />
                          <span>{item.name}</span>
                          {item.price > 0 && <span className="text-green-600 ml-1">€{item.price.toFixed(2)}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              )}

              {/* Close button */}
              <Button
                onClick={() => {
                  setImportDialogOpen(false);
                  resetImportDialog();
                }}
                className={`w-full ${importResults.errors.length === 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-600 hover:bg-slate-700'}`}
              >
                {importResults.errors.length === 0 ? 'Perfetto, chiudi' : 'Chiudi'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recipe Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedRecipe && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedRecipe.name}</DialogTitle>
                    <Badge variant="outline" className="mt-2 capitalize">
                      {selectedRecipe.category}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4">
                {selectedRecipe.description && (
                  <p className="text-slate-600">{selectedRecipe.description}</p>
                )}
                
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <Users className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                    <div className="font-semibold">{selectedRecipe.portions_produced}</div>
                    <div className="text-xs text-slate-500">Porzioni</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <Clock className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                    <div className="font-semibold">
                      {(selectedRecipe.preparation_time || 0) + (selectedRecipe.cooking_time || 0)} min
                    </div>
                    <div className="text-xs text-slate-500">Tempo totale</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <Euro className="w-5 h-5 mx-auto text-green-600 mb-1" />
                    <div className="font-semibold text-green-700">
                      €{selectedRecipe.food_cost_per_portion.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">Per porzione</div>
                  </div>
                </div>
                
                {selectedRecipe.ingredients?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Ingredienti</h4>
                    <ul className="space-y-1 text-sm">
                      {selectedRecipe.ingredients.map((ing, idx) => (
                        <li key={idx} className="flex justify-between py-1 border-b last:border-0">
                          <span>{ing.ingredient_name}</span>
                          <span className="text-slate-500">{ing.quantity} {ing.unit}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 pt-2 border-t text-sm text-green-700 font-medium">
                      Costo totale ingredienti: €{selectedRecipe.food_cost_total.toFixed(2)}
                    </div>
                  </div>
                )}
                
                {selectedRecipe.procedure && (
                  <div>
                    <h4 className="font-semibold mb-2">Procedimento</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedRecipe.procedure}</p>
                  </div>
                )}
                
                {selectedRecipe.allergens?.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Allergeni
                    </h4>
                    <p className="text-sm text-red-600 mt-1">{selectedRecipe.allergens.join(', ')}</p>
                  </div>
                )}
                
                {selectedRecipe.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">Note</h4>
                    <p className="text-sm text-slate-600">{selectedRecipe.notes}</p>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => handlePrintLabel(selectedRecipe)}
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Stampa Etichetta
                  </Button>
                  {canEditRecipes && (
                    <Button
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                      onClick={() => {
                        setDetailDialogOpen(false);
                        openEditRecipe(selectedRecipe);
                      }}
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Modifica
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Category Options Dialog */}
      <Dialog open={categoryOptionsDialogOpen} onOpenChange={setCategoryOptionsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Piatti: {selectedCategory?.name}
            </DialogTitle>
            <DialogDescription>
              Aggiungi i piatti predefiniti per questa categoria
            </DialogDescription>
          </DialogHeader>
          
          {selectedCategory && (
            <div className="flex-1 overflow-hidden flex flex-col gap-4 min-h-0">
              {/* Add new option - ONLY from recipes with search and multi-select */}
              {canEditSettingsDishes && (
                <div className="flex-shrink-0 p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                  <Label className="text-sm font-medium text-orange-800">Aggiungi piatti da Ricette</Label>
                  
                  {/* Search input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Cerca ricetta per nome..."
                      value={recipeSearchQuery}
                      onChange={(e) => setRecipeSearchQuery(e.target.value)}
                      className="pl-10 bg-white"
                      data-testid="search-recipe-for-dish"
                    />
                  </div>
                  
                  {/* Recipe list with checkboxes - fixed height with scroll */}
                  <div className="h-40 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                    {recipes.length === 0 ? (
                      <p className="p-3 text-sm text-slate-500 text-center">
                        Nessuna ricetta disponibile. Crea prima una ricetta nella tab "Ricette".
                      </p>
                    ) : (
                      (() => {
                        // Filter recipes by search query and exclude already added ones
                        const existingRecipeIds = (selectedCategory.options || [])
                          .filter(o => o.recipe_id)
                          .map(o => o.recipe_id);
                        const filteredRecipes = recipes.filter(r => 
                          r.name.toLowerCase().includes(recipeSearchQuery.toLowerCase()) &&
                          !existingRecipeIds.includes(r.id)
                        );
                        
                        if (filteredRecipes.length === 0) {
                          return (
                            <p className="p-3 text-sm text-slate-500 text-center">
                              {recipeSearchQuery ? 'Nessuna ricetta trovata' : 'Tutte le ricette sono già nel menu'}
                            </p>
                          );
                        }
                        
                        return filteredRecipes.map(recipe => (
                          <label 
                            key={recipe.id}
                            className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                          >
                            <Checkbox
                              checked={selectedRecipeIds.includes(recipe.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedRecipeIds(prev => [...prev, recipe.id]);
                                } else {
                                  setSelectedRecipeIds(prev => prev.filter(id => id !== recipe.id));
                                }
                              }}
                              data-testid={`recipe-checkbox-${recipe.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{recipe.name}</p>
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span className="text-green-600">
                                  €{(recipe.food_cost_per_portion || 0).toFixed(2)}/porz.
                                </span>
                                <span>•</span>
                                <span className="truncate">{categories.find(c => c.code === recipe.category)?.name || recipe.category}</span>
                              </div>
                            </div>
                          </label>
                        ));
                      })()
                    )}
                  </div>
                  
                  {/* Selected count and add button */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      {selectedRecipeIds.length > 0 
                        ? `${selectedRecipeIds.length} ricett${selectedRecipeIds.length === 1 ? 'a' : 'e'} selezionat${selectedRecipeIds.length === 1 ? 'a' : 'e'}`
                        : 'Seleziona le ricette da aggiungere'}
                    </span>
                    {selectedRecipeIds.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedRecipeIds([])}
                        className="text-slate-500 h-7"
                      >
                        Deseleziona tutto
                      </Button>
                    )}
                  </div>
                  
                  <Button 
                    onClick={handleAddOption} 
                    className="w-full bg-orange-500 hover:bg-orange-600" 
                    disabled={selectedRecipeIds.length === 0}
                    data-testid="add-dish-from-recipe-btn"
                  >
                    <Link className="w-4 h-4 mr-2" />
                    {selectedRecipeIds.length > 1 
                      ? `Aggiungi ${selectedRecipeIds.length} piatti al Menu`
                      : 'Aggiungi al Menu'}
                  </Button>
                  <p className="text-xs text-orange-700">
                    🔗 I piatti ereditano automaticamente allergeni e food cost dalle ricette.
                  </p>
                </div>
              )}
              
              {/* Options list - scrollable */}
              <div className="flex-1 min-h-0 flex flex-col">
                <Label className="text-sm font-medium flex-shrink-0 mb-2">
                  Piatti configurati ({selectedCategory.options?.length || 0})
                </Label>
                
                {(!selectedCategory.options || selectedCategory.options.length === 0) ? (
                  <p className="text-sm text-slate-500 italic p-4 bg-slate-50 rounded-lg text-center">
                    Nessun piatto configurato. Aggiungi i piatti sopra.
                  </p>
                ) : (
                  <div className="flex-1 overflow-y-auto space-y-2 min-h-[100px] max-h-[200px]">
                    {selectedCategory.options.map((option) => (
                      <div 
                        key={option.id}
                        className="p-3 bg-white border border-slate-200 rounded-lg"
                      >
                        {editingOption?.id === option.id ? (
                          /* Edit mode */
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <Input
                                value={editOptionName}
                                onChange={(e) => setEditOptionName(e.target.value)}
                                placeholder="Nome piatto"
                                className="flex-1"
                                autoFocus
                              />
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" size="sm" className="gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    {editOptionAllergens.length > 0 && `(${editOptionAllergens.length})`}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-3">
                                  <div className="space-y-3">
                                    <p className="text-xs font-medium text-slate-500">Allergeni</p>
                                    <div className="grid grid-cols-4 gap-2">
                                      {allergies.map(allergy => {
                                        const num = allergy.number;
                                        const hasContains = editOptionAllergens.includes(`${num}a`);
                                        const hasTraces = editOptionAllergens.includes(`${num}b`);
                                        
                                        return (
                                          <div key={allergy.id} className="flex flex-col gap-0.5">
                                            <Badge
                                              variant={hasContains ? "default" : "outline"}
                                              className={`cursor-pointer text-xs justify-center ${hasContains ? 'bg-amber-500' : ''}`}
                                              onClick={() => {
                                                const newList = editOptionAllergens.filter(a => a !== `${num}a` && a !== `${num}b`);
                                                if (!hasContains) {
                                                  setEditOptionAllergens([...newList, `${num}a`]);
                                                } else {
                                                  setEditOptionAllergens(newList);
                                                }
                                              }}
                                              title={`${allergy.name} (contiene)`}
                                            >
                                              {num}
                                            </Badge>
                                            <Badge
                                              variant={hasTraces ? "default" : "outline"}
                                              className={`cursor-pointer text-[10px] justify-center py-0 ${hasTraces ? 'bg-orange-400' : ''}`}
                                              onClick={() => {
                                                const newList = editOptionAllergens.filter(a => a !== `${num}a` && a !== `${num}b`);
                                                if (!hasTraces) {
                                                  setEditOptionAllergens([...newList, `${num}b`]);
                                                } else {
                                                  setEditOptionAllergens(newList);
                                                }
                                              }}
                                              title={`${allergy.name} (tracce)`}
                                            >
                                              tracce
                                            </Badge>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setEditingOption(null)}
                              >
                                Annulla
                              </Button>
                              <Button 
                                size="sm"
                                onClick={handleSaveEditOption}
                                className="bg-blue-700 hover:bg-blue-800"
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Salva
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* View mode */
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{option.name}</span>
                                {option.recipe_id && (
                                  <Badge variant="outline" className="text-[10px] bg-orange-50 border-orange-300 text-orange-700 gap-1">
                                    <Link className="w-3 h-3" />
                                    Ricetta
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                {option.allergens?.length > 0 && (
                                  <span className="text-amber-600 text-xs">
                                    Allergeni: {formatAllergensDisplay(option.allergens)}
                                  </span>
                                )}
                                {option.food_cost_per_portion && (
                                  <span className="text-green-600 text-xs font-medium">
                                    €{option.food_cost_per_portion.toFixed(2)}/porz.
                                  </span>
                                )}
                              </div>
                            </div>
                            {canEditSettingsDishes && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => startEditOption(option)}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteOption(option.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
